<script setup lang="ts">
// This computer as a node, in the desktop app (HRTH-103): pair it, share folders and run it in
// the background, without a terminal. The page pairs it the way Add a node does (a location and
// a one-time link carrying its key), and hands the link to the app, which runs the `hrth node`
// it bundles; it never becomes one of your devices. With `node` given, it manages that one.
import { computed, ref } from 'vue'
import { describeError } from '../api/http'
import type { StorageLocation } from '../api/types'
import { useSession } from '../stores/session'
import {
  folderName,
  nodePair,
  nodePickFolder,
  nodeService,
  nodeShare,
  nodeUnpair,
  serviceText,
  type DesktopNode,
  type StartAt,
} from '../lib/desktopNode'

const props = defineProps<{ node: DesktopNode; loc?: StorageLocation }>()
const emit = defineEmits<{ changed: []; cancel: [] }>()
const session = useSession()
const busy = ref(false)

/** Setting up: pair, then a folder, then the background service. */
const step = computed(() => (!props.node.paired ? 'pair' : !props.node.libraries.length ? 'folder' : !props.node.at ? 'service' : 'done'))
const name = ref('This computer')
const folder = ref<string | null>(null)
const libraryName = ref('')
const at = ref<StartAt>('login')

async function run(fn: () => Promise<unknown>, ok?: string): Promise<void> {
  busy.value = true
  try {
    await fn()
    if (ok) session.toast(ok)
    emit('changed')
  } catch (e) {
    session.toast(typeof e === 'string' ? e : describeError(e), 'error')
  } finally {
    busy.value = false
  }
}

const pair = () =>
  run(async () => {
    const { link } = await session.engine!.pairNode(name.value.trim())
    await nodePair(link)
  }, 'This computer is a node of yours now')

async function pick(): Promise<void> {
  try {
    const path = await nodePickFolder()
    if (!path) return
    folder.value = path
    libraryName.value = folderName(path)
  } catch (e) {
    session.toast(String(e), 'error')
  }
}

const share = () =>
  run(async () => {
    await nodeShare(folder.value!, libraryName.value.trim())
    folder.value = null
  }, `Shared ${libraryName.value.trim()}: share it with people from the Library`)

const install = () => run(() => nodeService('install', at.value), 'Serving in the background')
const toggle = () =>
  run(() => nodeService(props.node.service === 'active' ? 'stop' : 'start'))
const remove = () =>
  run(async () => {
    if (!confirm('Stop being a node on this computer? Its folders stay as they are; its libraries go offline.')) return
    await nodeUnpair()
    // Nothing on the server needs it any more: remove it there too.
    if (props.loc && props.loc.libraries === 0) await session.api.removeMyStorage(props.loc.id)
  }, 'This computer is no longer a node')
</script>

<template>
  <div class="space-y-3 border border-border p-3" data-testid="this-computer">
    <template v-if="step === 'pair'">
      <p class="mono text-text-muted">
        This computer serves folders you choose as libraries of yours, encrypting each file as it sends it, also while
        the app is closed. It isn't one of your devices: what runs in the background can't read your conversations.
      </p>
      <input v-model="name" class="input" maxlength="48" placeholder="What to call it" aria-label="Node name" data-testid="this-computer-name" />
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-ghost" @click="emit('cancel')">Cancel</button>
        <button type="button" class="btn-primary" :disabled="busy || !name.trim()" data-testid="this-computer-pair" @click="pair">Make it a node</button>
      </div>
    </template>

    <template v-else>
      <p class="mono text-text-muted" data-testid="this-computer-status">This computer · {{ serviceText(node) }}</p>
      <ul v-if="node.libraries.length" class="mono text-text-muted">
        <li v-for="l in node.libraries" :key="l.id" class="truncate" :title="l.path">↳ {{ l.path }}</li>
      </ul>

      <div v-if="folder" class="space-y-2" data-testid="this-computer-share">
        <p class="mono truncate text-text-muted" :title="folder">{{ folder }}</p>
        <input v-model="libraryName" class="input" maxlength="48" aria-label="Library name" data-testid="this-computer-library" />
        <div class="flex justify-end gap-2">
          <button type="button" class="btn-ghost" @click="folder = null">Cancel</button>
          <button type="button" class="btn-primary" :disabled="busy || !libraryName.trim()" data-testid="this-computer-share-save" @click="share">
            {{ busy ? 'Indexing…' : 'Share' }}
          </button>
        </div>
      </div>
      <button v-else type="button" class="btn-ghost text-xs" :disabled="busy" data-testid="this-computer-pick" @click="pick">
        {{ step === 'folder' ? 'Choose a folder to share…' : '+ Share another folder' }}
      </button>

      <div v-if="step === 'service'" class="space-y-2" data-testid="this-computer-service">
        <div class="label">Keep serving in the background</div>
        <label class="flex cursor-pointer items-start gap-2 text-sm">
          <input v-model="at" type="radio" value="login" class="mt-1" data-testid="this-computer-at-login" />
          <span>When I sign in<span class="mono block text-text-muted">While you're signed in to this computer, app open or not.</span></span>
        </label>
        <label class="flex cursor-pointer items-start gap-2 text-sm">
          <input v-model="at" type="radio" value="boot" class="mt-1" data-testid="this-computer-at-boot" />
          <span>When the computer starts<span class="mono block text-text-muted">Even when nobody's signed in, for a computer that stays on. Asks for an administrator's password.</span></span>
        </label>
        <button type="button" class="btn-primary" :disabled="busy" data-testid="this-computer-install" @click="install">Start serving</button>
      </div>

      <div v-if="step === 'done'" class="flex flex-wrap gap-2">
        <button type="button" class="btn-ghost text-xs" :disabled="busy" data-testid="this-computer-toggle" @click="toggle">
          {{ node.service === 'active' ? 'Stop' : 'Start' }}
        </button>
        <button type="button" class="btn-ghost text-xs" :disabled="busy" data-testid="this-computer-restart-at" @click="at = node.at === 'boot' ? 'login' : 'boot'; install()">
          Start {{ node.at === 'boot' ? 'when I sign in' : 'at boot' }} instead
        </button>
        <button type="button" class="btn-ghost text-xs hover:text-signal" :disabled="busy" data-testid="this-computer-remove" @click="remove">Stop being a node</button>
      </div>
    </template>
  </div>
</template>
